import Grid from "@mui/material/Grid2";
import { FormGroup } from "@cofano/cck";
import DatePicker from "components/cck/DatePicker";
import { intl } from "LocaleWrapper";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import DemurrageTermsSelect from "../select/DemurrageTermsSelect";
import DemurragePayableOverSelect from "../select/DemurragePayableOverSelect";
import TimeUnitSelect from "../select/TimeUnitSelect";
import CargoQuantityUnitSelect from "../select/CargoQuantityUnitSelect";
import { DemurrageCountingType, DemurragePayableOver, DemurrageTerms, TimeUnit, CargoQuantityUnit } from "__generated__/graphql";
import DemurrageCountingTypeSelect from "view/calculationmethods/components/select/DemurrageCountingTypeSelect";
import { Fragment, ReactNode } from "react";
import FormField from "components/cck/FormField";
import NumberInput from "components/cck/NumberInput";
import type { CalculationMethodFormByTypeProps } from "@schemas/calculationmethods/schema";

type FormValues = {
	agreements: {
		calculationMethod: {
			pricePerCargoQuantityUnit?: number;
			cargoQuantityUnit?: CargoQuantityUnit;
			demurrageTerms?: DemurrageTerms;
			demurragePayableOver?: DemurragePayableOver;
			timeUnit?: TimeUnit;
			price?: number;
			laytimeAllowed?: number;
			demurrageCountingType?: DemurrageCountingType;
			startCountingTime?: number;
			workingHoursSinceNomination?: number;
			workingDaysSinceNomination?: number;
		}
	}[];
}

type FormField = {
	id: string;
	render: () => ReactNode;
}


const DemurrageCalculationMethodForm = ({ index, currency, hiddenFields }: CalculationMethodFormByTypeProps) => {
	const { control, formState: { errors }, setValue } = useFormContext<FormValues>();

	const demurrageCountingType = useWatch({ control, name: `agreements.${index}.calculationMethod.demurrageCountingType` });
	const demurrageTerms = useWatch({ control, name: `agreements.${index}.calculationMethod.demurrageTerms` });
	const timeUnit = useWatch({ control, name: `agreements.${index}.calculationMethod.timeUnit` });

	const PricePerCargoQuantityUnitFormFields: FormField[] = [
		{
			id: "calculationMethod.pricePerCargoQuantityUnit",
			render: () => demurrageTerms === DemurrageTerms.PerContract && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.pricePerCargoQuantityUnit`}>
						<FormGroup label={intl.formatMessage({ id: `PRICE_PER_${timeUnit}_PER_UNIT` })} overflow="hidden">
							<Controller
								name={`agreements.${index}.calculationMethod.pricePerCargoQuantityUnit`}
								control={control}
								render={({ field }) => (
									<NumberInput
										{...field}
										addonBefore={currency.symbol} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.cargoQuantityUnit",
			render: () => demurrageTerms === DemurrageTerms.PerContract && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.cargoQuantityUnit`}>
						<FormGroup label={intl.formatMessage({ id: "CARGO_QUANTITY_UNIT" })} overflow="hidden">
							<Controller
								control={control}
								render={({ field }) => (
									<CargoQuantityUnitSelect {...field} />
								)}
								name={`agreements.${index}.calculationMethod.cargoQuantityUnit`} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
	];

	const DemurrageCalculationMethodFormFields: FormField[] = [
		{
			id: "calculationMethod.demurrageTerms",
			render: () => (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.demurrageTerms`}>
						<FormGroup label={intl.formatMessage({ id: "DEMURRAGE_TERMS" })} overflow="hidden" required>
							<Controller
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<DemurrageTermsSelect {...field} />
								)}
								name={`agreements.${index}.calculationMethod.demurrageTerms`} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.demurragePayableOver",
			render: () => demurrageTerms === DemurrageTerms.Ttb2010 && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.demurragePayableOver`}>
						<FormGroup label={intl.formatMessage({ id: "DEMURRAGE_PAYABLE_OVER" })} overflow="hidden" required>
							<Controller
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<DemurragePayableOverSelect {...field} />
								)}
								name={`agreements.${index}.calculationMethod.demurragePayableOver`} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.timeUnit",
			render: () => demurrageTerms === DemurrageTerms.PerContract && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.timeUnit`}>
						<FormGroup label={intl.formatMessage({ id: "TIME_UNIT" })} overflow="hidden" required>
							<Controller
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<TimeUnitSelect {...field} />
								)}
								name={`agreements.${index}.calculationMethod.timeUnit`} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.price",
			render: () => demurrageTerms === DemurrageTerms.PerContract && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.price`}>
						<FormGroup label={intl.formatMessage({ id: `PRICE_PER_${timeUnit}` })} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.calculationMethod.price`}
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<NumberInput
										{...field}
										addonBefore={currency.symbol} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.pricePerUnit",
			render: () => demurrageTerms === DemurrageTerms.PerContract && (
				<Grid container size={12} spacing={2}>
					{PricePerCargoQuantityUnitFormFields
						.map(x => x.render())
					}
				</Grid>
			),
		},
		{
			id: "calculationMethod.laytimeAllowed",
			render: () => demurrageTerms === DemurrageTerms.PerContract && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.laytimeAllowed`}>
						<FormGroup label={intl.formatMessage({ id: "LAYTIME_ALLOWED" })} overflow="hidden" required>
							<Controller
								rules={{ required: true }}
								name={`agreements.${index}.calculationMethod.laytimeAllowed`}
								control={control}
								render={({ field }) => <NumberInput {...field} addonAfter={intl.formatMessage({ id: "HOURS" })} />} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.demurrageCountingType",
			render: () => (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.demurrageCountingType`}>
						<FormGroup label={intl.formatMessage({ id: "DEMURRAGE_COUNTING_TYPE" })} overflow="hidden" required>
							<Controller
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<DemurrageCountingTypeSelect
										{...field}
										onChange={option => {
											field.onChange(option ?? DemurrageCountingType.ShortNotice);
											setValue(`agreements.${index}.calculationMethod.workingDaysSinceNomination`, undefined);
											setValue(`agreements.${index}.calculationMethod.workingHoursSinceNomination`, undefined);
											setValue(`agreements.${index}.calculationMethod.startCountingTime`, undefined);
										}} />
								)}
								name={`agreements.${index}.calculationMethod.demurrageCountingType`} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.startCountingTime",
			render: () => demurrageCountingType === DemurrageCountingType.StartCountingTime && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.startCountingTime`}>
						<FormGroup label={intl.formatMessage({ id: "START_COUNTING_TIME" })} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.calculationMethod.startCountingTime`}
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<DatePicker
										value={field.value ?? undefined}
										onChange={date => field.onChange(date ?? undefined)}
										showTimeInput />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.workingHoursSinceNomination",
			render: () => demurrageCountingType === DemurrageCountingType.WorkingHoursSinceNomination && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.workingHoursSinceNomination`}>
						<FormGroup label={intl.formatMessage({ id: "WORKING_HOURS_SINCE_NOMINATION" })} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.calculationMethod.workingHoursSinceNomination`}
								rules={{ required: true }}
								control={control}
								render={({ field }) => <NumberInput {...field} />} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.workingDaysSinceNomination",
			render: () => demurrageCountingType === DemurrageCountingType.WorkingDaysSinceNomination && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.workingDaysSinceNomination`}>
						<FormGroup label={intl.formatMessage({ id: "WORKING_DAYS_SINCE_NOMINATION" })} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.calculationMethod.workingDaysSinceNomination`}
								rules={{ required: true }}
								control={control}
								render={({ field }) => <NumberInput {...field} />} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
	];

	return (
		<>
			{DemurrageCalculationMethodFormFields
				.filter(x => !hiddenFields?.includes(x.id))
				.map(x => (
					<Fragment key={x.id}>
						{x.render()}
					</Fragment>
				))
			}
		</>
	);
};

export default DemurrageCalculationMethodForm;
